
import { processPdfFile, processEstimateText as processTextViaApi } from "@/services/api/pdf";

/**
 * Extract text from a PDF file
 * @param file PDF file to process
 * @returns Extracted text from the PDF
 */
export async function extractTextFromPDF(file: File): Promise<string> {
  try {
    // For backward compatibility, we'll return a simplified text string
    // but actually we'll process the entire PDF with the backend API
    const result = await processPdfFile(file);
    
    // Return the raw text from the processed result
    return result.raw_text || "No text extracted";
  } catch (error) {
    console.error("Error in extractTextFromPDF:", error);
    throw new Error(`PDF text extraction failed: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}

/**
 * Process a repair estimate PDF and extract structured data
 * @param file PDF file to process
 * @returns Structured data extracted from the estimate
 */
export async function processEstimate(file: File) {
  try {
    // Call the backend API to process the PDF
    const result = await processPdfFile(file);
    
    // Format the result for the frontend
    return {
      success: true,
      calculatedTotal: result.calculated_total || 0,
      data: {
        vehicle: result.vehicle,
        repairSections: result.repair_sections,
        summary: result.summary,
        repairInfo: result.repair_info
      },
      raw_text: result.raw_text
    };
  } catch (error) {
    console.error("Error in processEstimate:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to process PDF",
      data: null
    };
  }
}

/**
 * Process repair estimate text directly (useful for testing)
 * @param text Raw text to process
 * @returns Structured data extracted from the text
 */
export async function processEstimateText(text: string) {
  try {
    // Call the backend API to process the text
    const result = await processTextViaApi(text);
    
    // Format the result for the frontend
    return {
      success: true,
      calculatedTotal: result.calculated_total || 0,
      data: {
        vehicle: result.vehicle,
        repairSections: result.repair_sections,
        summary: result.summary,
        repairInfo: result.repair_info
      },
      raw_text: result.raw_text
    };
  } catch (error) {
    console.error("Error in processEstimateText:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to process text",
      data: null
    };
  }
}
